import axios, { AxiosInstance } from 'axios';
import { config } from '../config';
import {
  ChatResponse,
  Conversation,
  FileUploadResponse,
  UserPreferences,
} from '../types';

/**
 * A function that returns the current access token (or null if not available).
 * Injected from the component that has access to the Okta SDK so that each
 * request uses a fresh, non-expired token.
 */
export type TokenGetter = () => string | null | undefined;

/**
 * A function called when the backend returns 401 (token expired / invalid).
 * Injected from the authenticated component so the API client can trigger
 * a full Okta re-login without importing the Okta SDK here.
 */
export type OnUnauthorized = () => void;

class ApiClient {
  private client: AxiosInstance;
  private tokenGetter: TokenGetter = () => null;
  private onUnauthorized: OnUnauthorized | null = null;

  /**
   * Provide a token getter so the client always uses a fresh Okta access token.
   * Call this once from the authenticated component (e.g. Home.tsx) with:
   *   apiClient.setTokenGetter(() => oktaAuth.getAccessToken());
   */
  setTokenGetter(getter: TokenGetter): void {
    this.tokenGetter = getter;
  }

  /**
   * Provide a callback to invoke when the backend returns 401.
   * The callback should trigger a full Okta re-login (signInWithRedirect).
   * Call this once from the authenticated component (e.g. Home.tsx) with:
   *   apiClient.setOnUnauthorized(() => oktaAuth.signInWithRedirect());
   */
  setOnUnauthorized(handler: OnUnauthorized): void {
    this.onUnauthorized = handler;
  }

  constructor() {
    this.client = axios.create({
      baseURL: config.apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.tokenGetter();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle 401 — token expired or invalid.
    // When the backend rejects the token, trigger a full Okta re-login so the
    // user is never silently stuck with a broken session.
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && this.onUnauthorized) {
          this.onUnauthorized();
        }
        return Promise.reject(error);
      }
    );
  }

  // Chat API
  async sendMessage(
    message: string,
    conversationId?: string,
    fileKeys?: string[]
  ): Promise<ChatResponse> {
    // Use longer timeout for chat (especially with file analysis) - 10 minutes
    const response = await this.client.post('/api/chat', {
      message,
      conversationId,
      fileKeys,
    }, {
      timeout: 600000, // 10 minutes
    });
    return response.data;
  }

  // File Upload API
  async uploadFile(file: File): Promise<FileUploadResponse> {
    // Step 1: Request pre-signed URL
    const { data } = await this.client.post('/api/files/upload', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });

    // Step 2: Upload to S3 using pre-signed URL
    await axios.put(data.uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    return data;
  }

  // Conversations API
  async getConversations(): Promise<Conversation[]> {
    const response = await this.client.get('/api/conversations');
    return Array.isArray(response.data.conversations) ? response.data.conversations : [];
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await this.client.get(`/api/conversations/${conversationId}`);
    return response.data;
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await this.client.delete(`/api/conversations/${conversationId}`);
  }

  async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    await this.client.patch(`/api/conversations/${conversationId}/title`, { title });
  }

  async updateConversationStatus(conversationId: string, status: string): Promise<void> {
    await this.client.patch(`/api/conversations/${conversationId}/status`, { status });
  }

  // Preferences API
  async getPreferences(): Promise<UserPreferences> {
    const response = await this.client.get('/api/preferences');
    return response.data;
  }

  async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
    await this.client.put('/api/preferences', preferences);
  }

  // Module Search API (optional)
  async searchModules(provider: string, query: string): Promise<any> {
    const response = await this.client.get('/api/modules/search', {
      params: { provider, q: query },
    });
    return response.data;
  }

  // Code Preview — apply a change request to the current files without
  // touching the main conversation history.
  async previewChange(changeRequest: string, files: Array<{ path: string; content: string }>): Promise<{ message: string; files?: Array<{ path: string; content: string }> }> {
    const response = await this.client.post('/api/preview/change', {
      changeRequest,
      files,
    }, {
      timeout: 300000, // 5 minutes
    });
    return response.data;
  }

  // Code Preview — push the reviewed (possibly edited) files directly to ADO.
  // No worker Lambda polling needed — this endpoint writes + commits in one shot.
  async pushToAdo(
    conversationId: string,
    files: Array<{ path: string; content: string }>,
    adoRepo: string,
    adoProject?: string,
    isEdit?: boolean,
    editTargetBranch?: string,
    prTitle?: string,
    prDescription?: string
  ): Promise<{ success: boolean; adoLink: string; repoName: string; branch: string; folder?: string; repoCloneUrl?: string; commitSha?: string; prUrl?: string; isEdit?: boolean; tfvarsFile?: string; backendConfigFile?: string }> {
    const response = await this.client.post('/api/generate/push-to-ado', {
      conversationId,
      files,
      adoRepo,
      adoProject,
      isEdit,
      editTargetBranch,
      prTitle,
      prDescription,
    }, {
      timeout: 120000, // 2 minutes — git clone + commit
    });
    return response.data;
  }

  // Generic HTTP methods (for deployment and other features)
  async get(url: string): Promise<any> {
    const response = await this.client.get(url);
    return response.data;
  }

  async post(url: string, data?: any): Promise<any> {
    const response = await this.client.post(url, data);
    return response.data;
  }
}

export const apiClient = new ApiClient();
